/**
 * Homepage scroll overlay — 4 full-viewport HTML sections
 * layered on top of the fixed Three.js canvas.
 *
 * Sections:
 * 1. Hero — "Meet Your Team"
 * 2. Team Grows — "Your Team Grows With You"
 * 3. Real Work — "Real Work. Real Deliverables."
 * 4. CTA — "HATCH a Coworker"
 */

let overlay: HTMLElement | null = null;

/**
 * Create and inject the homepage overlay into the DOM.
 * Returns the overlay element.
 */
export function createHomepageOverlay(): HTMLElement {
  if (overlay) return overlay;

  overlay = document.createElement('div');
  overlay.id = 'homepage-overlay';

  overlay.innerHTML = `
    <div class="hp-logo">Hatchlings</div>

    <section class="hp-section hp-hero">
      <div class="hp-content">
        <h1 class="hp-title">Meet Hatchlings!</h1>
        <p class="hp-subtitle">Your new AI coworkers</p>
        <div class="hp-scroll-hint">
          <span class="hp-scroll-arrow">&#8595;</span>
          <span>Scroll to explore</span>
        </div>
      </div>
    </section>

    <section class="hp-section hp-team">
      <div class="hp-content">
        <h2 class="hp-heading">Your Team Grows With You</h2>
        <p class="hp-text">Add Hatchlings as you need them. Each one specializes in a different skill — engineering, design, marketing, strategy.</p>
      </div>
    </section>

    <section class="hp-section hp-work">
      <div class="hp-content">
        <h2 class="hp-heading">Real Work. Real Deliverables.</h2>
        <p class="hp-text">Code, designs, copy, strategy — delivered right to your desk. Not just chat. Actual output you can use.</p>
      </div>
    </section>

    <section class="hp-section hp-cta">
      <div class="hp-content">
        <h2 class="hp-heading">Ready to deploy your Hatchlings?</h2>
        <button id="hp-enter-btn">Early Access</button>
        <div id="hp-password-gate" class="hp-password-gate">
          <div class="hp-password-row">
            <input type="password" id="hp-password-input" class="hp-password-input" placeholder="Access code" autocomplete="off" spellcheck="false" />
            <button id="hp-password-submit" class="hp-password-submit">Enter</button>
          </div>
          <div id="hp-password-error" class="hp-password-error">Invalid access code</div>
        </div>
      </div>
    </section>
  `;

  document.body.appendChild(overlay);

  // Wire up the Early Access button → password gate
  const enterBtn = overlay.querySelector('#hp-enter-btn') as HTMLButtonElement | null;
  const passwordGate = overlay.querySelector('#hp-password-gate') as HTMLElement | null;
  const passwordInput = overlay.querySelector('#hp-password-input') as HTMLInputElement | null;
  const passwordSubmit = overlay.querySelector('#hp-password-submit') as HTMLButtonElement | null;
  const passwordError = overlay.querySelector('#hp-password-error') as HTMLElement | null;

  if (enterBtn && passwordGate && passwordInput && passwordSubmit && passwordError) {
    // Click "Early Access" → reveal password input
    enterBtn.addEventListener('click', () => {
      enterBtn.style.display = 'none';
      passwordGate.classList.add('visible');
      setTimeout(() => passwordInput.focus(), 300);
    });

    // Submit password
    const submitPassword = async () => {
      const value = passwordInput.value.trim();
      if (!value) { passwordInput.focus(); return; }

      passwordSubmit.disabled = true;
      passwordSubmit.textContent = '...';
      passwordError.classList.remove('visible');
      passwordInput.classList.remove('error');

      try {
        const res = await fetch('/api/auth/access-code', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: value }),
        });
        const data = await res.json();
        if (data.valid === true) {
          localStorage.setItem('hatchlings-access', 'true');
          // Dispatch enter event to trigger auth flow → office
          window.dispatchEvent(new CustomEvent('enter-from-homepage'));
        } else {
          showPasswordError();
        }
      } catch {
        showPasswordError();
      }
    };

    const showPasswordError = () => {
      passwordSubmit.disabled = false;
      passwordSubmit.textContent = 'Enter';
      passwordInput.classList.add('error');
      passwordError.classList.add('visible');
      // Shake animation
      const row = passwordGate.querySelector('.hp-password-row') as HTMLElement;
      if (row) {
        row.classList.remove('shake');
        void row.offsetWidth; // force reflow
        row.classList.add('shake');
      }
      passwordInput.select();
    };

    passwordSubmit.addEventListener('click', submitPassword);
    passwordInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submitPassword();
    });
    passwordInput.addEventListener('input', () => {
      passwordInput.classList.remove('error');
      passwordError.classList.remove('visible');
    });

    // Auto-trigger login if redirected from office with ?login=1
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('login') === '1') {
      console.log('[Homepage] Auto-triggering login from ?login=1');
      // If already has access cookie, go straight through
      if (localStorage.getItem('hatchlings-access') === 'true') {
        window.dispatchEvent(new CustomEvent('enter-from-homepage'));
      } else {
        // Show password gate
        enterBtn.style.display = 'none';
        passwordGate.classList.add('visible');
        setTimeout(() => passwordInput.focus(), 300);
      }
    }
  } else {
    console.error('[Homepage] Could not find password gate elements');
  }

  return overlay;
}

/**
 * Remove the homepage overlay from the DOM and clean up.
 */
export function removeHomepageOverlay(): void {
  if (overlay) {
    overlay.remove();
    overlay = null;
  }
}

/**
 * Check if the homepage overlay is currently in the DOM.
 */
export function isHomepageOverlayActive(): boolean {
  return overlay !== null && overlay.parentElement !== null;
}
